import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { supabase } from "@/lib/supabase";
import { useAnalytics } from "@/composables/useAnalytics";
import { ANALYTICS_EVENTS, USER_PROPERTIES, SUPER_PROPERTIES } from "@/utils/analyticsEvents";

export const useAuthStore = defineStore("auth", () => {
  const user = ref(null);
  const session = ref(null);
  const loading = ref(false);
  const isSigningOut = ref(false); // Flag para ignorar eventos INITIAL_SESSION después de signOut

  const isAuthenticated = computed(() => !!user.value);
  
  // Initialize analytics
  const analytics = useAnalytics();

  function isAuthRefreshNetworkError(error) {
    if (!error) return false;

    const name = String(error.name || "");
    const message = String(error.message || "");

    return (
      name === "AuthRetryableFetchError" ||
      (message.includes("Failed to fetch") && message.includes("supabase.co"))
    );
  }

  async function clearLocalSessionState() {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (_error) {
      // Ignore local cleanup errors; we still force in-memory cleanup below.
    } finally {
      session.value = null;
      user.value = null;
    }
  }

  async function signIn(email, password) {
    loading.value = true;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      user.value = data.user;
      session.value = data.session;
      
      // Track login event
      analytics.track(ANALYTICS_EVENTS.USER_LOGGED_IN, {
        login_method: 'email',
        device_type: getDeviceType()
      });
      
      return { user: data.user, error: null };
    } catch (error) {
      return { user: null, error };
    } finally {
      loading.value = false;
    }
  }

  async function signUp(email, password, fullName) {
    loading.value = true;
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });
      if (error) throw error;
      user.value = data.user;
      session.value = data.session;
      
      // Track registration event
      analytics.track(ANALYTICS_EVENTS.USER_REGISTERED, {
        email: data.user?.email,
        registration_date: new Date().toISOString(),
        signup_source: 'organic' // Could be enhanced with UTM params
      });
      
      return { user: data.user, error: null };
    } catch (error) {
      return { user: null, error };
    } finally {
      loading.value = false;
    }
  }

  async function signOut() {
    loading.value = true;
    isSigningOut.value = true; // Activar flag para ignorar INITIAL_SESSION
    
    // Detener verificación periódica de sesión PRIMERO
    stopSessionCheck();
    
    try {
      // Track logout event before clearing state
      analytics.track(ANALYTICS_EVENTS.USER_LOGGED_OUT);
      
      // Limpiar suscripción de créditos antes de cerrar sesión
      const { useCreditsStore } = await import("./credits");
      const creditsStore = useCreditsStore();
      await creditsStore.unsubscribeFromCredits();
      creditsStore.reset();

      // Resetear proyectos también
      const { useProjectsStore } = await import("./projects");
      const projectsStore = useProjectsStore();
      projectsStore.reset();

      // Agregar timeout para evitar que se cuelgue indefinidamente
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("signOut timeout")), 3000)
      );

      let error;
      try {
        const result = await Promise.race([
          supabase.auth.signOut(),
          timeoutPromise,
        ]);
        error = result.error;
      } catch (timeoutError) {
        // Si hay timeout, forzar limpieza de estado de todas formas
        console.warn("signOut timeout, forcing state cleanup");
        error = null; // Continuar con la limpieza
      }

      if (error && error.message !== "signOut timeout") throw error;

      // Reset analytics state
      analytics.reset();

      // El onAuthStateChange manejará la actualización del estado automáticamente
      // pero establecemos el estado aquí también para respuesta inmediata
      user.value = null;
      session.value = null;
    } catch (error) {
      console.error("Error signing out:", error);
      // Asegurarse de limpiar el estado incluso si hay error
      user.value = null;
      session.value = null;
    } finally {
      loading.value = false;
    }
  }

  // Flag para evitar llamadas concurrentes a getSession
  let getSessionPromise = null;

  async function getSession() {
    // Si ya hay una llamada en progreso, esperarla
    if (getSessionPromise) {
      return getSessionPromise;
    }

    getSessionPromise = (async () => {
      try {
        // Agregar timeout para evitar que se cuelgue indefinidamente
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("getSession timeout")), 5000)
        );

        let sessionData, error;
        try {
          const result = await Promise.race([
            supabase.auth.getSession(),
            timeoutPromise,
          ]);
          sessionData = result.data?.session;
          error = result.error;
        } catch (fetchError) {
          // Si es timeout, retornar null en lugar de lanzar error
          if (fetchError?.message === "getSession timeout") {
            return null;
          }
          if (isAuthRefreshNetworkError(fetchError)) {
            await clearLocalSessionState();
            return null;
          }
          throw fetchError;
        }
        if (error) throw error;
        session.value = sessionData;
        user.value = sessionData?.user ?? null;

        // Si hay sesión, cargar el conteo de proyectos y créditos
        if (sessionData?.user) {
          const { useProjectsStore } = await import("./projects");
          const projectsStore = useProjectsStore();
          await projectsStore.fetchProjectCount();

          // Cargar créditos (auto-suscripción incluida en fetchBalance)
          const { useCreditsStore } = await import("./credits");
          const creditsStore = useCreditsStore();
          await creditsStore.fetchBalance();
        }

        return sessionData;
      } catch (error) {
        // No loggear AbortError como error crítico
        if (error.name !== "AbortError") {
          console.error("Error getting session:", error);
        }
        return null;
      } finally {
        getSessionPromise = null;
      }
    })();

    return getSessionPromise;
  }

  /**
   * Refrescar la sesión actual si está expirada o próxima a expirar
   * Retorna true si la sesión es válida (refrescada o no), false si no hay sesión
   */
  async function refreshSession() {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error("Error refreshing session:", error);
        if (isAuthRefreshNetworkError(error)) {
          await clearLocalSessionState();
          return false;
        }
        // Si el refresh falla, limpiar estado
        session.value = null;
        user.value = null;
        return false;
      }

      if (data?.session) {
        session.value = data.session;
        user.value = data.session.user;
        
        // Recargar datos del usuario después de refrescar sesión
        const { useProjectsStore } = await import("./projects");
        const projectsStore = useProjectsStore();
        await projectsStore.fetchProjectCount(data.session.user);

        const { useCreditsStore } = await import("./credits");
        const creditsStore = useCreditsStore();
        await creditsStore.fetchBalance(data.session.user);
        // subscribeToCredits se llama automáticamente en fetchBalance
        
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error in refreshSession:", error);
      if (isAuthRefreshNetworkError(error)) {
        await clearLocalSessionState();
      }
      return false;
    }
  }

  /**
   * Verificar si la sesión es válida y refrescarla si es necesario
   * Útil antes de operaciones críticas
   */
  async function ensureValidSession() {
    // Si no hay sesión, intentar obtenerla
    if (!session.value) {
      await getSession();
    }

    // Si aún no hay sesión, retornar false
    if (!session.value) {
      return false;
    }

    // Verificar si la sesión está próxima a expirar (menos de 5 minutos)
    const expiresAt = session.value.expires_at;
    if (expiresAt) {
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiresAt - now;
      
      // Si expira en menos de 5 minutos, refrescar
      if (timeUntilExpiry < 300) {
        return await refreshSession();
      }
    }

    return true;
  }

  // No llamar getSession() al inicializar el store
  // Confiamos completamente en onAuthStateChange para manejar el estado inicial
  // Esto evita problemas de timing con la inicialización de Supabase

  // Helper: Detect device type
  function getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  // Intervalo para verificar y refrescar sesión automáticamente
  let sessionCheckInterval = null;

  /**
   * Iniciar verificación periódica de sesión
   * Verifica cada 2 minutos si la sesión está próxima a expirar
   */
  function startSessionCheck() {
    // Limpiar intervalo existente si hay uno
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval);
    }

    // Verificar cada 2 minutos (120000 ms)
    sessionCheckInterval = setInterval(async () => {
      if (!session.value) return;

      const expiresAt = session.value.expires_at;
      if (!expiresAt) return;

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiresAt - now;

      // Si expira en menos de 10 minutos, refrescar proactivamente
      if (timeUntilExpiry < 600) {
        console.log('Session expiring soon, refreshing...');
        await refreshSession();
      }
    }, 120000); // 2 minutos
  }

  /**
   * Detener verificación periódica de sesión
   */
  function stopSessionCheck() {
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval);
      sessionCheckInterval = null;
    }
  }

  // Escuchar cambios de autenticación
  supabase.auth.onAuthStateChange(async (_event, newSession) => {
    // Ignorar INITIAL_SESSION si estamos en proceso de signOut
    if (isSigningOut.value && _event === "INITIAL_SESSION" && newSession) {
      return;
    }

    // Si recibimos SIGNED_OUT, desactivar la flag
    if (_event === "SIGNED_OUT") {
      isSigningOut.value = false;
    }

    // Actualizar estado primero
    session.value = newSession;
    user.value = newSession?.user ?? null;

    // Si el token se refrescó automáticamente, solo recargar datos sin ejecutar todo el flujo
    if (_event === "TOKEN_REFRESHED" && newSession?.user) {
      // Recargar créditos
      try {
        const { useCreditsStore } = await import("./credits");
        const creditsStore = useCreditsStore();
        await creditsStore.fetchBalance(newSession.user);
      } catch (error) {
        console.error("Error reloading credits after token refresh:", error);
      }

      // Recargar conteo de proyectos
      try {
        const { useProjectsStore } = await import("./projects");
        const projectsStore = useProjectsStore();
        await projectsStore.fetchProjectCount(newSession.user);
      } catch (error) {
        console.error("Error reloading projects after token refresh:", error);
      }
      
      // No continuar con el resto del flujo de autenticación
      return;
    }

    // Si el usuario se autentica (SIGNED_IN o INITIAL_SESSION), cargar datos completos
    if (newSession?.user && (_event === "SIGNED_IN" || _event === "INITIAL_SESSION")) {
      // Iniciar verificación periódica de sesión
      startSessionCheck();
      
      // Ejecutar todo el flujo de forma NO BLOQUEANTE
      // Esto permite que el login continúe sin esperar a que termine todo
      (async () => {
        // Identify user in analytics
        try {
          // Get profile data for user properties
          const { data: profile } = await supabase
            .from('profiles')
            .select('plan_type')
            .eq('user_id', newSession.user.id)
            .maybeSingle();

          // Identify user with Mixpanel
          analytics.identify(newSession.user.id, {
            [USER_PROPERTIES.EMAIL]: newSession.user.email,
            [USER_PROPERTIES.PLAN_TYPE]: profile?.plan_type || 'free',
            [USER_PROPERTIES.REGISTRATION_DATE]: newSession.user.created_at,
            [USER_PROPERTIES.LAST_LOGIN]: new Date().toISOString()
          });

          // Set super properties
          analytics.setSuperProperties({
            [SUPER_PROPERTIES.PLAN_TYPE]: profile?.plan_type || 'free'
          });
        } catch (error) {
          console.error("Error identifying user in analytics:", error);
        }

        // Aceptar invitaciones pendientes para este email
        if (newSession.user.email) {
          try {
            const rpcPromise = supabase.rpc(
              'accept_pending_invitations', 
              { p_email: newSession.user.email }
            )
            const timeoutPromise = new Promise((resolve) => 
              setTimeout(() => resolve({ data: 0, error: new Error('timeout') }), 3000)
            )
            
            const { data: acceptedCount, error: invError } = await Promise.race([
              rpcPromise,
              timeoutPromise
            ])
            
            if (invError && invError.message !== 'timeout') {
              console.error("Error accepting pending invitations:", invError)
            } else if (acceptedCount > 0) {
              // Track collaboration acceptance
              analytics.track(ANALYTICS_EVENTS.COLLABORATION_ACCEPTED, {
                invitations_accepted: acceptedCount
              })
            }
          } catch (error) {
            console.error("Error accepting pending invitations:", error)
          }
        }

        // Cargar proyectos
        try {
          await new Promise(resolve => setTimeout(resolve, 500))
          
          const { useProjectsStore } = await import("./projects");
          const projectsStore = useProjectsStore();
          
          projectsStore.fetchProjectCount(newSession.user).catch(() => {
            // Silenciar el error durante la inicialización
          });
        } catch (error) {
          // Silenciar errores durante la inicialización
        }

        // Cargar créditos
        try {
          const { useCreditsStore } = await import("./credits");
          const creditsStore = useCreditsStore();
          await creditsStore.fetchBalance(newSession.user);
        } catch (error) {
          console.error("Error loading credits after auth:", error);
        }
      })();
    } else {
      // Si se desautentica, resetear proyectos y créditos
      // Detener verificación periódica de sesión
      stopSessionCheck();
      try {
        const { useProjectsStore } = await import("./projects");
        const projectsStore = useProjectsStore();
        projectsStore.reset();
      } catch (error) {
        console.error("Error resetting projects:", error);
      }

      try {
        const { useCreditsStore } = await import("./credits");
        const creditsStore = useCreditsStore();
        await creditsStore.unsubscribeFromCredits();
        creditsStore.reset();
      } catch (error) {
        console.error("Error resetting credits:", error);
      }
    }
  });

  /**
   * Enviar email de recuperación de contraseña
   * @param {string} email - Email del usuario
   * @returns {Promise<{success: boolean, error: Error|null}>}
   */
  async function resetPassword(email) {
    loading.value = true;
    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      
      if (error) throw error;
      
      return { success: true, error: null };
    } catch (error) {
      console.error("Error sending reset password email:", error);
      return { success: false, error };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Actualizar contraseña del usuario autenticado
   * @param {string} newPassword - Nueva contraseña
   * @returns {Promise<{success: boolean, error: Error|null}>}
   */
  async function updatePassword(newPassword) {
    loading.value = true;
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      
      if (error) throw error;
      
      return { success: true, error: null };
    } catch (error) {
      console.error("Error updating password:", error);
      return { success: false, error };
    } finally {
      loading.value = false;
    }
  }

  return {
    user,
    session,
    loading,
    isAuthenticated,
    signIn,
    signUp,
    signOut,
    getSession,
    refreshSession,
    ensureValidSession,
    startSessionCheck,
    stopSessionCheck,
    resetPassword,
    updatePassword,
  };
});
